import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { ReactNode, RefObject, useEffect, useState } from "react";

import theme from "../../theme";
import { Comparison } from "./APComparison";

type APComparisonStickyHeaderProps<T> = {
  comparison: Comparison<T>;
  sortedItems: T[];
  itemWidth: number;
  labelItemWidth: number;
  headerRowRef: RefObject<HTMLTableRowElement>;
  scrollRef: RefObject<HTMLDivElement>;
  scrollNavigation: ReactNode;
  zIndex: number;
};

const APComparisonStickyHeader = <T,>({
  comparison,
  sortedItems,
  itemWidth,
  labelItemWidth,
  headerRowRef,
  scrollRef,
  scrollNavigation,
  zIndex,
}: APComparisonStickyHeaderProps<T>) => {
  // Add state to track horizontal scroll position
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState<T[]>([]);

  // Add state to track vertical scroll and header visibility
  const [shouldShowStickyHeader, setShouldShowStickyHeader] = useState(false);

  // Add effect to track horizontal scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        setHorizontalScrollLeft(scrollRef.current.scrollLeft);
      }
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [scrollRef.current]);

  // Calculate which columns are currently visible

  useEffect(() => {
    if (scrollRef.current?.clientWidth) {
      const startIndex = Math.round(horizontalScrollLeft / itemWidth);
      const containerWidth = scrollRef.current?.clientWidth
        ? scrollRef.current.clientWidth - labelItemWidth
        : 0;
      const visibleCount = Math.ceil(containerWidth / itemWidth);

      setVisibleColumns(
        sortedItems.slice(startIndex, startIndex + visibleCount)
      );
    }
  }, [horizontalScrollLeft, sortedItems, scrollRef.current?.clientWidth]);

  // Add effect to track header row position and determine sticky header visibility
  useEffect(() => {
    const updateHeaderVisibility = () => {
      if (headerRowRef.current) {
        const rect = headerRowRef.current.getBoundingClientRect();

        // Show sticky header when the top of the header row is out of view (above the viewport)
        const isTopOutOfView = rect.top < -8;

        setShouldShowStickyHeader(isTopOutOfView);
      }
    };

    // Update on scroll and resize
    updateHeaderVisibility();
    window.addEventListener("scroll", updateHeaderVisibility);
    window.addEventListener("resize", updateHeaderVisibility);

    return () => {
      window.removeEventListener("scroll", updateHeaderVisibility);
      window.removeEventListener("resize", updateHeaderVisibility);
    };
  }, [headerRowRef.current]);

  const renderStickyHeader = () => {
    if (!shouldShowStickyHeader) return null;

    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          zIndex,
          backgroundColor: "white",
          borderBottom: "1px solid",
          borderColor: "outlineVariant.main",
          display: "flex",
          width: "inherit",
          paddingLeft: "16px",
          marginLeft: "-16px",
          paddingRight: "16px",
        }}
      >
        <Box sx={{ position: "relative", display: "flex", width: "100%" }}>
          {scrollNavigation}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {/* Label column - shows the headerCriteria label */}
            <Box
              sx={{
                minWidth: `${labelItemWidth}px`,
                maxWidth: `${labelItemWidth}px`,
                px: 1,
                py: 1,
                display: "flex",
                alignItems: "center",
              }}
            />

            {/* Visible data columns - show the headerCriteria values for each visible item */}
            {visibleColumns.map((item, index) => {
              const value = item[comparison.headerCriteria];
              const criterion = comparison.criteria[comparison.headerCriteria];
              return (
                <Box
                  key={`sticky-header-${index}`}
                  sx={{
                    minWidth: `${itemWidth}px`,
                    maxWidth: `${itemWidth}px`,
                    px: 1,
                    py: 1,
                    pb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderColor: "outlineVariant.main",
                    backgroundColor:
                      comparison.highlightWinner &&
                      index === 0 &&
                      horizontalScrollLeft === 0
                        ? alpha(theme.palette.tertiaryGreen.main, 0.05)
                        : undefined,
                  }}
                >
                  {criterion?.valueRenderer ? (
                    criterion.valueRenderer(item)
                  ) : (
                    <Typography variant="labelSmall">
                      {criterion?.valueFormatter
                        ? criterion.valueFormatter(value)
                        : String(value)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  return <>{visibleColumns.length > 0 && renderStickyHeader()}</>;
};

export default APComparisonStickyHeader;
